import React, { useState, useRef, useEffect, useMemo } from 'react'
import { X, CheckCircle, Layers, ChevronRight, Check, User, MapPin, FileSignature, Upload, MessageSquare, Sparkles, ListChecks } from 'lucide-react'
import { SaleWizard } from './SaleWizard'
import { Button } from '../ui/Button'
import { cn } from '@/lib/utils'
import toast from 'react-hot-toast'
import { useHubAccess } from '../../hooks/useHubAccess'
import { useAuth } from '../../contexts/AuthContext'
import { Progress } from '../ui/ProgressBar'

type TabKey = 'initial' | 'exchange' | 'acquisition'
type Phase1Step = 'exchange-info' | 'upload-documents' | 'exchangers' | 'property-details' | 'signatures'

export const ExchangeWizardHub: React.FC<{ onClose: () => void; onSectionComplete?: (section: TabKey) => void; initialTab?: TabKey }> = ({ onClose, onSectionComplete, initialTab }) => {
  const { user } = useAuth()
  const [phase1Step, setPhase1Step] = useState<Phase1Step>('exchange-info')
  const [phase1StepsCompleted, setPhase1StepsCompleted] = useState<Record<Phase1Step, boolean>>({ 'exchange-info': false, 'upload-documents': false, exchangers: false, 'property-details': false, signatures: false })
  const [showTicketModal, setShowTicketModal] = useState(false)
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null)
  const [ticketMessage, setTicketMessage] = useState('')
  const [isSalesHubSubmitted, setIsSalesHubSubmitted] = useState(() => localStorage.getItem('sales_hub_submitted') === 'true')
  const [isInEscrow, setIsInEscrow] = useState(false)
  const [hasPreliminaryTitleReport, setHasPreliminaryTitleReport] = useState(false)
  const controlsRef = useRef<any | null>(null)
  const identificationAccess = useHubAccess('identification', user)
  const salesCompletionPercent = useMemo(() => {
    if (isSalesHubSubmitted) return 100
    const total = Object.keys(phase1StepsCompleted).length
    const completed = Object.values(phase1StepsCompleted).filter(Boolean).length
    return total > 0 ? Math.round((completed / total) * 100) : 0
  }, [phase1StepsCompleted, isSalesHubSubmitted])
  
  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.classList.add('modal-open')
    return () => {
      document.body.classList.remove('modal-open')
    }
  }, [])

  useEffect(() => {
    if (!user?.id) return
    const savedStep = localStorage.getItem(`sales_hub_phase1_step_${user.id}`) as Phase1Step | null
    if (savedStep) {
      setPhase1Step(savedStep)
    }
  }, [user?.id])

  useEffect(() => {
    if (!user?.id) return
    localStorage.setItem(`sales_hub_phase1_step_${user.id}`, phase1Step)
  }, [user?.id, phase1Step])
  
  // Check if Sales Hub can be submitted (all steps complete including signatures)
  const canSubmitSalesHub = useMemo(() => {
    if (isSalesHubSubmitted) return false
    const allStepsComplete = Object.values(phase1StepsCompleted).every(v => v === true)
    return allStepsComplete
  }, [phase1StepsCompleted, isSalesHubSubmitted])

  // Update step completion status based on form state
  useEffect(() => {
    const updateCompletion = () => {
      if (!controlsRef.current?.getPhase1StepCompletion) return
      const completion = controlsRef.current.getPhase1StepCompletion()
      if (completion) {
        setPhase1StepsCompleted(completion)
      }
      
      // Update escrow state
      if (controlsRef.current?.getEscrowState) {
        const escrowState = controlsRef.current.getEscrowState()
        if (escrowState) {
          setIsInEscrow(escrowState.isInEscrow)
          setHasPreliminaryTitleReport(escrowState.hasPreliminaryTitleReport)
        }
      }
    }
    
    // Check immediately
    const timeout = setTimeout(updateCompletion, 100)
    
    // Check periodically to update as user fills in fields
    const interval = setInterval(updateCompletion, 1000)
    
    return () => {
      clearTimeout(timeout)
      clearInterval(interval)
    }
  }, [phase1Step]) // Re-check when step changes

  // Listen for sales hub completion
  useEffect(() => {
    const handleSalesHubCompleted = () => {
      setIsSalesHubSubmitted(true)
      // Force a re-render by updating state
      setPhase1StepsCompleted(prev => ({ ...prev }))
      // Trigger hub access recalculation
      window.dispatchEvent(new CustomEvent('wizardDataUpdated'))
    }
    window.addEventListener('salesHubCompleted', handleSalesHubCompleted)
    return () => window.removeEventListener('salesHubCompleted', handleSalesHubCompleted)
  }, [])

  useEffect(() => {
    const handleAutoSaveUpdate = () => {
      const savedTime = controlsRef.current?.getLastSavedAt?.()
      if (savedTime) {
        setLastSavedAt(savedTime)
      }
    }
    window.addEventListener('wizardDataUpdated', handleAutoSaveUpdate)
    handleAutoSaveUpdate()
    return () => window.removeEventListener('wizardDataUpdated', handleAutoSaveUpdate)
  }, [])
  // Also check localStorage periodically to catch state changes
  useEffect(() => {
    const checkSubmission = () => {
      const submitted = localStorage.getItem('sales_hub_submitted') === 'true'
      if (submitted !== isSalesHubSubmitted) {
        setIsSalesHubSubmitted(submitted)
      }
    }
    
    // Check immediately
    checkSubmission()
    
    // Check periodically
    const interval = setInterval(checkSubmission, 500)
    return () => clearInterval(interval)
  }, [isSalesHubSubmitted])

  const handleRequestEdit = () => {
    if (!isSalesHubSubmitted) {
      toast.error('Please submit the Sales Hub section before requesting edits')
      return
    }
    setShowTicketModal(true)
  }

  const handleSubmitTicket = () => {
    if (!ticketMessage.trim()) {
      toast.error('Please enter a description of the changes needed')
      return
    }
    
    // Mock ticket submission
    const ticketNumber = `TICKET-${Date.now().toString().slice(-6)}`
    
    toast.success(`Edit request submitted! Ticket #${ticketNumber}`)
    
    // Store ticket in localStorage (mock)
    const tickets = JSON.parse(localStorage.getItem('edit_tickets') || '[]')
    tickets.push({
      ticketNumber,
      message: ticketMessage,
      status: 'pending',
      createdAt: new Date().toISOString(),
      section: 'Sales Hub'
    })
    localStorage.setItem('edit_tickets', JSON.stringify(tickets))
    
    // Reset and close
    setTicketMessage('')
    setShowTicketModal(false)
  }

  const handlePhase1StepComplete = (step: Phase1Step) => {
    setPhase1StepsCompleted(prev => ({ ...prev, [step]: true }))
    // If all Phase 1 steps are complete, notify parent
    const allComplete = Object.values({ ...phase1StepsCompleted, [step]: true }).every(v => v)
    if (allComplete) {
      onSectionComplete?.('initial')
    }
  }

  const getPhase1StepNumber = (step: Phase1Step): number => {
    const stepMap: Record<Phase1Step, number> = {
      'exchange-info': 1,
      'upload-documents': 2,
      'exchangers': 3,
      'property-details': 4,
      'signatures': 5
    }
    return stepMap[step] || 1
  }

  const getTotalPhase1Steps = (): number => {
    // Phase 1 has 5 steps: exchange-info, upload-documents, exchangers, property-details, signatures
    return 5
  }

  const getPhase1StepTitle = (step: Phase1Step): string => {
    const titleMap: Record<Phase1Step, string> = {
      'exchange-info': 'Exchange Information',
      'upload-documents': 'Upload Documents',
      'exchangers': 'Exchanger Information',
      'property-details': 'Property & Buyer Details',
      'signatures': 'E-Sign Documents'
    }
    return titleMap[step]
  }

  return (
    <div className="modal-overlay modal-overlay-sales-hub">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-5xl max-h-full overflow-hidden flex flex-col">
        {/* Header */}
        <div className="border-b border-slate-200 dark:border-slate-700 px-6 py-4 bg-white dark:bg-slate-800">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-3">
              <Layers className="h-6 w-6 text-green-600 dark:text-green-400" />
              <div>
                <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Sales Hub</h2>
                <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">Upload → extract → confirm → e-sign.</p>
                {isSalesHubSubmitted && (
                  <div className="flex items-center space-x-2 mt-1">
                    <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                    <span className="text-xs text-green-600 dark:text-green-400 font-medium">Completed & Read-Only</span>
                  </div>
                )}
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-secondary-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
              <X className="h-5 w-5 text-secondary-600 dark:text-slate-400" />
            </button>
          </div>
          {/* Completion Progress */}
          <div className="mt-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-slate-600 dark:text-slate-400">Completion</span>
              <span className="text-xs font-semibold text-green-600 dark:text-green-400">{salesCompletionPercent}%</span>
            </div>
            <Progress value={salesCompletionPercent} className="h-2 [&>div]:!bg-gradient-to-r [&>div]:!from-green-600 [&>div]:!via-green-500 [&>div]:!to-green-600 [&>div]:!shadow-[0_0_10px_rgba(34,197,94,0.6)]" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-hidden bg-white dark:bg-slate-800 flex flex-col">
          <div className="flex-1 min-h-0 overflow-y-auto p-4">
            <div className="mb-3 border-b border-slate-200 dark:border-slate-700 pb-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Quick Steps</h3>
                <span className="text-xs text-slate-500 dark:text-slate-500">
                  Step {getPhase1StepNumber(phase1Step)} of {getTotalPhase1Steps()}
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                {(['exchange-info', 'upload-documents', 'exchangers', 'property-details', 'signatures'] as Phase1Step[]).map((step) => (
                  <button
                    key={step}
                    type="button"
                    onClick={() => setPhase1Step(step)}
                    className={cn(
                      "w-full flex items-center justify-center gap-1 px-2 md:px-3 py-1.5 rounded-md text-[clamp(10px,1.1vw,12px)] font-medium transition-all",
                      isSalesHubSubmitted 
                        ? phase1Step === step
                          ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 cursor-pointer"
                          : "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30 cursor-pointer"
                        : phase1Step === step
                        ? "bg-green-600 dark:bg-green-600 text-white shadow-sm"
                        : phase1StepsCompleted[step]
                        ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30"
                        : "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600"
                    )}
                  >
                    {step === 'exchange-info' && <Sparkles className="h-3.5 w-3.5 flex-shrink-0" />}
                    {step === 'upload-documents' && <Upload className="h-3.5 w-3.5 flex-shrink-0" />}
                    {step === 'exchangers' && <User className="h-3.5 w-3.5 flex-shrink-0" />}
                    {step === 'property-details' && <MapPin className="h-3.5 w-3.5 flex-shrink-0" />}
                    {step === 'signatures' && <FileSignature className="h-3.5 w-3.5 flex-shrink-0" />}
                    <span className="text-center leading-none whitespace-nowrap">{getPhase1StepTitle(step)}</span>
                    {(phase1StepsCompleted[step] || isSalesHubSubmitted) && <CheckCircle className="h-3.5 w-3.5 flex-shrink-0" />}
                  </button>
                ))}
              </div>
            </div>
            <SaleWizard 
              embedded 
              hideFooter 
              exposeControls={(api) => (controlsRef.current = api)} 
              mode="phase1" 
              phase1Step={phase1Step}
              onClose={onClose} 
              onComplete={() => handlePhase1StepComplete(phase1Step)}
              onPhase1StepChange={setPhase1Step}
            />
          </div>
        </div>

        {/* Footer - global navigation */}
        <div className="border-t border-slate-200 dark:border-slate-700 px-4 md:px-6 py-3 md:py-4 bg-white dark:bg-slate-800">
          <div className="flex items-center justify-between gap-2">
            {isSalesHubSubmitted ? (
              <div className="flex items-center space-x-2 min-w-0 flex-1">
                <CheckCircle className="h-5 w-5 text-green-500 dark:text-green-400 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-green-700 dark:text-green-300 truncate">Sales Hub Completed</p>
                  <p className="text-xs text-green-600 dark:text-green-400 hidden md:block">
                    {identificationAccess.canAccess 
                      ? 'Identification Hub is now unlocked' 
                      : 'Please verify your identity to unlock the next phase'}
                  </p>
                </div>
              </div>
            ) : (
              <Button 
                onClick={handleRequestEdit}
                disabled={true}
                variant="outline"
                className="border-slate-300 text-slate-400 dark:border-slate-600 dark:text-slate-500 cursor-not-allowed opacity-50"
                title="Request Edit"
              >
                <MessageSquare className="h-4 w-4 mr-1" />
                Request Edit
              </Button>
            )}
            <div className="flex items-center gap-2 flex-shrink-0">
              {!isSalesHubSubmitted && (
                <div className="flex items-center gap-2 rounded-full border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-1 text-xs text-slate-600 dark:text-slate-300">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                  </span>
                  <span>
                    {lastSavedAt ? `Auto-saved ${new Date(lastSavedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'Auto-save on'}
                  </span>
                </div>
              )}
              <Button 
                className="bg-green-600 hover:bg-green-700 dark:bg-green-600 dark:hover:bg-green-700 text-white disabled:opacity-50 disabled:cursor-not-allowed" 
                onClick={() => {
                  console.log('Submit button clicked', { controlsRef: controlsRef.current, isSalesHubSubmitted, canSubmitSalesHub })
                  try {
                    if (controlsRef.current?.submit) {
                      console.log('Calling submit function')
                      controlsRef.current.submit()
                    } else {
                      console.error('Submit function not available', controlsRef.current)
                      toast.error('Wizard not ready. Please wait a moment and try again.')
                    }
                  } catch (error) {
                    console.error('Error submitting sales hub:', error)
                    toast.error('Failed to submit. Please try again.')
                  }
                }}
                disabled={isSalesHubSubmitted || !canSubmitSalesHub}
                title={!canSubmitSalesHub && !isSalesHubSubmitted ? 'Please complete all steps and sign both documents before submitting' : (isSalesHubSubmitted ? 'Submitted' : 'Submit')}
              >
                <Layers className="h-4 w-4 mr-1" />
                Submit Hub
              </Button>
              {isSalesHubSubmitted && (
                <Button 
                  onClick={handleRequestEdit}
                  variant="outline"
                  className="group relative border-2 border-green-600/30 text-green-600 hover:border-green-600 hover:bg-green-600/10 dark:border-green-400/40 dark:text-green-400 dark:hover:border-green-400 dark:hover:bg-green-400/20 hover:shadow-md transition-all duration-200 px-2 md:px-3"
                  title="Request Edit"
                >
                  <MessageSquare className="h-4 w-4 md:mr-2" />
                  <span className="hidden md:inline">Request Edit</span>
                </Button>
              )}
              <Button 
                onClick={onClose} 
                variant="outline"
                className="border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700"
                title="Close"
              >
                <X className="h-4 w-4 mr-1" />
                Close
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Request Ticket Modal */}
      {showTicketModal && (
        <div className="modal-overlay modal-overlay-nested">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden">
            {/* Header */}
            <div className="border-b border-slate-200 dark:border-slate-700 px-6 py-4 flex items-center justify-between bg-gradient-to-r from-green-600/10 to-green-500/10 dark:from-green-600/20 dark:to-green-500/20">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-600/20 dark:bg-green-600/30 rounded-lg">
                  <MessageSquare className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Request Edit</h3>
                  <p className="text-xs text-slate-600 dark:text-slate-400">Sales Hub Section</p>
                </div>
              </div>
              <button 
                onClick={() => setShowTicketModal(false)} 
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-slate-600 dark:text-slate-400" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  What changes do you need?
                </label>
                <textarea
                  value={ticketMessage}
                  onChange={(e) => setTicketMessage(e.target.value)}
                  placeholder="Please describe the edits or corrections needed for your Sales Hub information..."
                  className="w-full px-4 py-3 border-2 border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-green-600 focus:border-green-600 dark:focus:ring-green-400 dark:focus:border-green-400 transition-all resize-none"
                  rows={6}
                  autoFocus
                />
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-full bg-blue-500 dark:bg-blue-600 flex items-center justify-center">
                      <span className="text-white text-sm font-bold">ℹ️</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">
                      What happens next?
                    </h4>
                    <p className="text-xs text-blue-800 dark:text-blue-200">
                      Our support team will review your request and contact you within 1-2 business days. You'll receive a confirmation email with your ticket number.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-slate-200 dark:border-slate-700 px-6 py-4 bg-slate-50 dark:bg-slate-900 flex items-center justify-between">
              <Button
                onClick={() => setShowTicketModal(false)}
                variant="outline"
                className="border-slate-300 dark:border-slate-600"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitTicket}
                disabled={!ticketMessage.trim()}
                className="bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 dark:from-green-600 dark:to-green-500 dark:hover:from-green-700 dark:hover:to-green-600 text-white border-0 shadow-lg"
              >
                <Check className="h-4 w-4 mr-2" />
                Submit Request
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}


